import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  FaBook,
  FaChevronDown,
  FaCog,
  FaGraduationCap,
  FaHeadphones,
  FaHome,
  FaLayerGroup,
  FaPenAlt,
  FaSignOutAlt,
  FaStickyNote,
  FaUsers,
  FaUpload,
} from "react-icons/fa";
import { config } from "../../config";

// ─── nav tree ────────────────────────────────────────────────────────────────
const NAV = [
  {
    label: "Overview",
    items: [
      { id: "dashboard", label: "Dashboard", icon: FaHome, menu: "dashboard" },
    ],
  },
  {
    label: "Content",
    items: [
      {
        id: "mcq",
        label: "MCQ Editor",
        icon: FaPenAlt,
        children: [
          {
            id: "subjects",
            label: "Subjects",
            icon: FaGraduationCap,
            children: [
              { label: "Add & View Subjects", menu: "addSubject" },
            ],
          },
          {
            id: "questions",
            label: "Questions",
            icon: FaLayerGroup,
            children: [
              { label: "Create Questions", menu: "addQuestion" },
              { label: "Questions Overview", menu: "questionOverview" },
            ],
          },
        ],
      },
      {
        id: "ielts",
        label: "IELTS",
        icon: FaHeadphones,
        children: [
          { label: "Subject & Practice", menu: "ieltsManager" },
          { label: "Writing Submissions", menu: "ieltsWriting" },
          { label: "Results Manager", menu: "ieltsResults" },
        ],
      },
    ],
  },
  {
    label: "Resources",
    items: [
      {
        id: "notes",
        label: "Notes",
        icon: FaStickyNote,
        children: [
          { label: "All Notes", menu: "addNote" },
        ],
      },
      {
        id: "flash",
        label: "Flash Cards",
        icon: FaBook,
        children: [
          { label: "Flash Card Overview", menu: "flashOverview" },
        ],
      },
    ],
  },
  {
    label: "Admin",
    items: [
      { id: "users", label: "Users", icon: FaUsers, menu: "users" },
    ],
  },
];

// ─── helpers ─────────────────────────────────────────────────────────────────
const allMenus = (items, acc = []) => {
  items.forEach((item) => {
    if (item.menu) acc.push(item.menu);
    if (item.children) allMenus(item.children, acc);
    if (item.items) allMenus(item.items, acc);
  });
  return acc;
};

// ─── logo edit modal ─────────────────────────────────────────────────────────

const LogoEditModal = ({ currentLogo, currentInitials, onClose, onSave }) => {
  const [preview, setPreview] = useState(currentLogo || "");
  const [textLogo, setTextLogo] = useState(currentInitials || "");
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-gray-900">Change Logo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-center justify-center h-20 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
            {preview ? (
              <img src={preview} alt="Logo preview" className="h-14 object-contain" />
            ) : (
              <span className="text-gray-400 text-sm">{textLogo || "No logo"}</span>
            )}
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Text Logo (fallback)</label>
            <input value={textLogo} onChange={(e) => setTextLogo(e.target.value)} maxLength={6}
              placeholder="e.g. DA" maxLength={6}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-bold text-center uppercase focus:border-indigo-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Upload Image</label>
            <input type="file" accept="image/*" onChange={handleFileChange}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-bold file:bg-indigo-50 file:text-indigo-700" />
          </div>
        </div>
        <div className="px-5 py-4 bg-gray-50 flex gap-2 justify-end">
          <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={() => onSave({ file, text: textLogo.trim() || "A" })}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────
const DashboardSidebar = ({ isOpen, selectedMenu, setSelectedMenu, toggleSidebar, isMobile }) => {
  const [expanded, setExpanded] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("expandedMenus") || "{}");
    } catch {
      return {};
    }
  });

  const [showLogoEdit, setShowLogoEdit] = useState(false);
  const [logoSrc, setLogoSrc] = useState(localStorage.getItem("sidebarLogoImg") || "");
  const [logoInitials, setLogoInitials] = useState(localStorage.getItem("sidebarLogoText") || "A");

  useEffect(() => {
    localStorage.setItem("expandedMenus", JSON.stringify(expanded));
  }, [expanded]);

  const handleSaveLogo = ({ file, text }) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = e.target.result;
        setLogoSrc(data);
        localStorage.setItem("sidebarLogoImg", data);
        localStorage.setItem("ieltsLogoImg", data);
      };
      reader.readAsDataURL(file);
    }
    setLogoInitials(text || "A");
    localStorage.setItem("sidebarLogoText", text || "A");
    localStorage.setItem("ieltsLogoText", text || "A");
  };

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const select = (menu) => {
    setSelectedMenu(menu);
    localStorage.setItem("selectedMenu", menu);
    if (isMobile) toggleSidebar();
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${config.apiUrl}/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {}
    ["token", "selectedMenu", "expandedMenus"].forEach((k) => localStorage.removeItem(k));
    window.location.href = "/login";
  };

  return (
    <aside
      className={`
        fixed md:relative inset-y-0 left-0 z-30
        w-64 min-h-screen flex flex-col
        bg-[#1a1c2e] text-white
        transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 h-16 px-5 border-b border-white/10 flex-shrink-0 group relative">
        <button
          onClick={() => setShowLogoEdit(true)}
          title="Change logo"
          className="absolute inset-0 flex items-center gap-3 px-5 w-full hover:bg-white/5 rounded-none transition-colors cursor-pointer"
        >
          {logoSrc ? (
            <img src={logoSrc} alt="" className="w-8 h-8 rounded-lg object-contain bg-white" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center font-bold text-sm text-white shrink-0">
              {logoInitials}
            </div>
          )}
          <div>
            <p className="font-bold text-sm leading-tight text-white">Admin Panel</p>
            <p className="text-xs text-white/40 leading-tight">Learning Platform</p>
          </div>
          <FaCog className="ml-auto text-white/30 group-hover:text-white/60 text-xs" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {NAV.map((section) => (
          <div key={section.label} className="mb-5">
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-white/30">
              {section.label}
            </p>
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <NavItem
                  key={item.id}
                  item={item}
                  selectedMenu={selectedMenu}
                  expanded={expanded}
                  toggle={toggle}
                  select={select}
                  depth={0}
                />
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div className="px-3 pb-4 flex-shrink-0 border-t border-white/10 pt-3">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <FaSignOutAlt className="text-base flex-shrink-0" />
          Logout
        </button>
      </div>

      {/* Logo edit modal */}
      {showLogoEdit && (
        <LogoEditModal
          currentLogo={logoSrc}
          currentInitials={logoInitials}
          onClose={() => setShowLogoEdit(false)}
          onSave={handleSaveLogo}
        />
      )}
    </aside>
  );
};

// ─── recursive nav item ───────────────────────────────────────────────────────
const NavItem = ({ item, selectedMenu, expanded, toggle, select, depth }) => {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isActive = !hasChildren && selectedMenu === item.menu;
  const isExpanded = expanded[item.id];

  const Icon = item.icon;
  const indent = depth * 12;

  if (!hasChildren) {
    // Leaf item
    return (
      <li>
        <button
          onClick={() => select(item.menu)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
            isActive
              ? "bg-indigo-600 text-white font-semibold shadow-sm"
              : "text-white/60 hover:text-white hover:bg-white/10"
          }`}
          style={{ paddingLeft: `${12 + indent}px` }}
        >
          {Icon && <Icon className="text-base flex-shrink-0" />}
          <span className="truncate">{item.label}</span>
        </button>
      </li>
    );
  }

  // Branch item
  return (
    <li>
      <button
        onClick={() => toggle(item.id)}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/10 transition-all duration-150"
        style={{ paddingLeft: `${12 + indent}px` }}
      >
        {Icon && <Icon className="text-base flex-shrink-0" />}
        <span className="flex-1 text-left truncate">{item.label}</span>
        <FaChevronDown
          className={`text-xs transition-transform duration-200 flex-shrink-0 ${
            isExpanded ? "rotate-180 text-indigo-400" : ""
          }`}
        />
      </button>

      {isExpanded && (
        <ul className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <NavItem
              key={child.id || child.menu}
              item={child}
              selectedMenu={selectedMenu}
              expanded={expanded}
              toggle={toggle}
              select={select}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default DashboardSidebar;
